import { t } from 'i18next';

import {
  type FinancialStatementRawItem,
  getAssetOverviewFinancialsUrl,
  getStatementDisplayLabel,
  normalizeFinancialStatementItems,
} from '../financial-statements-item/financialStatementUtils';
import type {
  ConversationInventory,
  FormattedConversationInventory,
} from '../services/inventory-service';
import { htmlNodes } from './htmlNodes';
import {
  type KgExportGroup,
  kgExportGroupsToHtml,
  kgExportGroupsToPlainText,
  resolveKgForSourcesSection,
} from './kgSourcesForCopy';

type SourcesSection = {
  title: string;
  textItems: string[];
  htmlItems: string[];
  kgExportGroups?: KgExportGroup[];
};

export type InventoryContent = { html: string; text: string };

export interface BuildInventoryContentParams {
  inventory?: ConversationInventory | FormattedConversationInventory;
  baseUrl?: string;
  rawFinancialStatementItems?: FinancialStatementRawItem[];
}

const getItems = <T>(collection: { items?: T[] } | undefined): T[] =>
  collection?.items ?? [];

export const buildInventoryContent = async ({
  inventory,
  baseUrl,
  rawFinancialStatementItems,
}: BuildInventoryContentParams = {}): Promise<InventoryContent> => {
  if (!inventory || Object.keys(inventory).length === 0) {
    return { html: '', text: '' };
  }

  const { kgExportGroups } = await resolveKgForSourcesSection(inventory);

  const insightTitles =
    inventory && 'insightTitles' in inventory
      ? inventory.insightTitles
      : undefined;

  const economicCalendarEntityNames =
    inventory && 'economicCalendarEntityNames' in inventory
      ? inventory.economicCalendarEntityNames
      : undefined;
  let html = '';
  let text = '';

  const entities = getItems(inventory?.entity).map(i => i.tag);
  const timeseries = getItems(inventory?.timeseries).map(i => i.snake);
  const webLinks = getItems(inventory?.['web-links']).flatMap(
    i => i.urls ?? []
  );
  const financialStatementItems = normalizeFinancialStatementItems(
    getItems(inventory?.['financial-statement'])
  );
  const rawItemsForLinks = normalizeFinancialStatementItems(
    rawFinancialStatementItems ?? getItems(inventory?.['financial-statement'])
  );
  const financialStatementsText = financialStatementItems.map(item =>
    item.statement
      ? `${item.entityTag} - ${getStatementDisplayLabel(item.statement)}`
      : item.entityTag
  );
  const financialStatementsHtml = baseUrl
    ? financialStatementItems.map((item, i) => {
        const rawItem = rawItemsForLinks[i];
        const entityTag = rawItem?.entityTag ?? item.entityTag;
        const displayText = item.statement
          ? `${item.entityTag} - ${getStatementDisplayLabel(item.statement)}`
          : item.entityTag;
        return htmlNodes.a(
          displayText,
          getAssetOverviewFinancialsUrl(entityTag, baseUrl, item.statement)
        );
      })
    : financialStatementsText;

  const earningsInsights = getItems(inventory?.['earnings-insight']).map(
    item => item.title
  );
  const catalystInsights = getItems(inventory?.['catalyst-insight']).map(
    item => item.title
  );

  const externalData = getItems(inventory?.['external-data']).flatMap(
    i => i.links ?? []
  );

  const newsHeadlines = getItems(inventory?.news).map(item => item.title);

  const insightHeadlines = getItems(inventory?.insight).map(
    item => insightTitles?.[item.id] ?? item.id
  );

  const watchlistNames = getItems(inventory?.watchlist).map(item => item.name);

  const economicCalendarLines = getItems(inventory?.['economic-calendar']).map(
    item => {
      if (item.entity_tag) {
        const companyName =
          economicCalendarEntityNames?.[item.entity_tag] ?? item.entity_tag;
        return t('calendar:companyEarnings', { company: companyName });
      }

      if ((item.countries?.length ?? 0) > 1) {
        return t('calendar:economicEventsMultiple', {
          countries: item.countries?.join(', ') ?? '',
        });
      }

      if (!item.countries?.length && !item.categories?.length) {
        return t('calendar:allEconomicEvents');
      }

      if (!item.countries?.length && item.categories?.length) {
        return t('calendar:economicEventsByCategory', {
          categories: item.categories.join(', '),
        });
      }

      const countryName = item.countries?.[0]
        ? t(`countries:${item.countries[0]}`, {
            defaultValue: item.countries[0],
          })
        : '';
      return t('calendar:economicEvents', { country: countryName });
    }
  );

  const knowledgeGraphSections =
    kgExportGroups != null && kgExportGroups.length > 0
      ? [
          {
            title: t('chat:inventory.sections.knowledgeGraph'),
            textItems: [],
            htmlItems: [],
            kgExportGroups,
          },
        ]
      : [];

  const documentSearchItems = getItems(inventory?.['document-search']).filter(
    item => (item.documents?.length ?? 0) > 0
  );

  const citationLines = documentSearchItems.map(item => {
    const rawQuestion = item.question ?? '';
    const question = rawQuestion ? `"${rawQuestion}"` : '';
    const count = item.documents?.length ?? 0;
    return t('chat:inventory.citationsForQuestionFoundDocuments', {
      question,
      count,
    });
  });

  const oneDriveItems = getItems(inventory?.['microsoft_onedrive']);
  const outlookItems = getItems(inventory?.['microsoft_outlook']);
  const oneNoteItems = getItems(inventory?.['microsoft_onenote']);

  const sections: SourcesSection[] = [
    {
      title: t('chat:inventory.sections.entities', { count: entities.length }),
      textItems: entities,
      htmlItems: entities,
    },
    {
      title: t('chat:inventory.sections.timeSeries', {
        count: timeseries.length,
      }),
      textItems: timeseries,
      htmlItems: timeseries,
    },
    {
      title: t('chat:inventory.sections.financials', {
        count: financialStatementItems.length,
      }),
      textItems: financialStatementsText,
      htmlItems: financialStatementsHtml,
    },
    {
      title: t('chat:inventory.sections.earningsInsights', {
        count: earningsInsights.length,
      }),
      textItems: earningsInsights,
      htmlItems: earningsInsights,
    },
    {
      title: t('chat:inventory.sections.catalystInsights', {
        count: catalystInsights.length,
      }),
      textItems: catalystInsights,
      htmlItems: catalystInsights,
    },
    {
      title: t('chat:inventory.sections.oneDrive', {
        count: oneDriveItems.length,
      }),
      textItems: oneDriveItems.map(i => i.name),
      htmlItems: oneDriveItems.map(i => i.name),
    },
    {
      title: t('chat:inventory.sections.outlook', {
        count: outlookItems.length,
      }),
      textItems: outlookItems.map(i => i.name),
      htmlItems: outlookItems.map(i => i.name),
    },
    {
      title: t('chat:inventory.sections.onenote', {
        count: oneNoteItems.length,
      }),
      textItems: oneNoteItems.map(i => i.name),
      htmlItems: oneNoteItems.map(i => i.name),
    },
    {
      title: t('chat:inventory.sections.watchlist', {
        count: watchlistNames.length,
      }),
      textItems: watchlistNames,
      htmlItems: watchlistNames,
    },
    {
      title: t('chat:inventory.sections.economicCalendar', {
        count: economicCalendarLines.length,
      }),
      textItems: economicCalendarLines,
      htmlItems: economicCalendarLines,
    },
    ...knowledgeGraphSections,
    {
      title: t('chat:inventory.sections.citations', {
        count: citationLines.length,
      }),
      textItems: citationLines,
      htmlItems: citationLines,
    },
    {
      title: t('chat:inventory.sections.insights', {
        count: insightHeadlines.length,
      }),
      textItems: insightHeadlines,
      htmlItems: insightHeadlines,
    },
    {
      title: t('chat:inventory.sections.news', {
        count: newsHeadlines.length,
      }),
      textItems: newsHeadlines,
      htmlItems: newsHeadlines,
    },
    {
      title: t('chat:inventory.sections.externalData', {
        count: externalData.length,
      }),
      textItems: externalData,
      htmlItems: externalData
        .filter((url): url is string => url != null)
        .map(url => htmlNodes.a(url, url)),
    },
    {
      title: t('chat:inventory.sections.webLinks', { count: webLinks.length }),
      textItems: webLinks,
      htmlItems: webLinks
        .filter((url): url is string => url != null)
        .map(url => htmlNodes.a(url, url)),
    },
  ];

  const nonEmpty = sections.filter(
    s =>
      (s.kgExportGroups != null && s.kgExportGroups.length > 0) ||
      s.textItems.length > 0
  );

  if (!nonEmpty.length) {
    return { html, text };
  }

  text += `\n\n## ${t('chat:inventory.sources')}`;
  html += htmlNodes.h2(t('chat:inventory.sources'));

  nonEmpty.forEach(s => {
    if (s.kgExportGroups != null && s.kgExportGroups.length > 0) {
      text +=
        `\n\n### ${s.title}\n` + kgExportGroupsToPlainText(s.kgExportGroups);
      html += htmlNodes.h3(s.title) + kgExportGroupsToHtml(s.kgExportGroups);
      return;
    }

    text += `\n\n### ${s.title}\n` + s.textItems.map(v => `- ${v}`).join('\n');
    html += htmlNodes.h3(s.title);
    html += htmlNodes.unorderedList(
      s.htmlItems
        .filter(v => v !== null && v !== undefined)
        .map(v => htmlNodes.unorderedListItem(v))
        .join('')
    );
  });

  return { html, text };
};
